import React, { useCallback, useEffect, useState } from "react";
import DistributorApi from "../../utils/api/DistributorApi";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import usedStyles from "./UsedSoldPins.module.css";
import Pagination from "../../components/list/Pagination";
import { logPinMarkedAsUsed } from "../../utils/googleSheets/pinUsageSheetService";

const normalizeRows = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const sortPinsOldestFirst = (firstPin, secondPin) => {
  const firstTime = Date.parse(firstPin?.created_at || "");
  const secondTime = Date.parse(secondPin?.created_at || "");

  if (!Number.isNaN(firstTime) && !Number.isNaN(secondTime)) {
    return firstTime - secondTime;
  }

  if (!Number.isNaN(firstTime)) {
    return -1;
  }

  if (!Number.isNaN(secondTime)) {
    return 1;
  }

  return Number(firstPin?.id ?? 0) - Number(secondPin?.id ?? 0);
};

function ActiveSoldPins() {
  const [pins, setPins] = useState([]);
  const [fixedAgents, setFixedAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedPinIds, setSelectedPinIds] = useState([]);
  const [bulkCountInput, setBulkCountInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [loadingPins, setLoadingPins] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [markingId, setMarkingId] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ completed: 0, total: 0 });
  const [confirmAction, setConfirmAction] = useState(null);

  const token = useSelector((state) => state.userData.loginData.token);
  const distributor = useSelector((state) => state.userData.personalInfo);
  const loginData = useSelector((state) => state.userData.loginData);

  const logSuccessfulPinUsage = (pin) => {
    // Logging intentionally happens only after mark-as-used resolves. It is
    // non-blocking so an unavailable spreadsheet cannot change the API result.
    logPinMarkedAsUsed({
      distributorName: distributor?.fullname || loginData?.fullname,
      agentName: pin?.agent_name,
      pinId: pin?.id,
      pin: pin?.pin,
    });
  };

  const fetchAllAgents = useCallback(async () => {
    let page = 1;
    let lastAvailablePage = 1;
    let allAgents = [];

    while (page <= lastAvailablePage) {
      const response = await DistributorApi.getAllAgents({
        token,
        page_no: page,
      });
      const data = response.data?.data;
      allAgents = [...allAgents, ...normalizeRows(data)];
      lastAvailablePage = Number(data?.last_page ?? 1);
      page += 1;
    }

    return allAgents;
  }, [token]);

  const fetchAllActiveSoldPins = useCallback(async () => {
    let page = 1;
    let lastAvailablePage = 1;
    let allPins = [];

    while (page <= lastAvailablePage) {
      const response = await DistributorApi.getActiveSoldPinList({
        token,
        page,
      });
      const data = response.data?.data;
      allPins = [...allPins, ...normalizeRows(data)];
      lastAvailablePage = Number(data?.last_page ?? 1);
      page += 1;
    }

    return allPins;
  }, [token]);

  const fetchPins = useCallback(async () => {
    try {
      setLoadingPins(true);

      if (selectedAgent) {
        // This is intentionally not ordinary list filtering: bulk actions may
        // select and mark any of this agent's active pins, including records
        // beyond the currently displayed server page. Until the API exposes an
        // agent filter, load this bounded selection dataset and keep paging off.
        const allPins = await fetchAllActiveSoldPins();
        const agentPins = allPins.filter(
          (pin) => pin?.agent_name === selectedAgent
        );

        setPins(agentPins);
        setLastPage(1);
        setHasNextPage(false);
        setHasPrevPage(false);
      } else {
        const response = await DistributorApi.getActiveSoldPinList({
          token,
          page: currentPage,
        });
        const data = response.data?.data;
        const rows = normalizeRows(data);

        setPins(rows);
        setLastPage(data?.last_page ?? null);
        setHasNextPage(!!data?.next_page_url);
        setHasPrevPage(!!data?.prev_page_url);
      }

      setSelectedRow(null);
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Failed to load pins", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
    } finally {
      setLoadingPins(false);
    }
  }, [currentPage, fetchAllActiveSoldPins, selectedAgent, token]);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        setLoadingAgents(true);
        const agents = await fetchAllAgents();
        setFixedAgents(
          agents.sort((firstAgent, secondAgent) =>
            (firstAgent?.fullname || "").localeCompare(
              secondAgent?.fullname || ""
            )
          )
        );
      } catch (error) {
        toast.error(
          error?.response?.data?.message ?? "Failed to load fixed agents",
          {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "dark",
          }
        );
      } finally {
        setLoadingAgents(false);
      }
    };

    loadAgents();
  }, [fetchAllAgents]);

  useEffect(() => {
    setSelectedRow(null);
    setSelectedPinIds([]);
    setBulkCountInput("");
  }, [selectedAgent]);

  useEffect(() => {
    if (selectedAgent && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    fetchPins();
  }, [currentPage, fetchPins, selectedAgent]);

  useEffect(() => {
    setSelectedPinIds((currentSelectedIds) =>
      currentSelectedIds.filter((pinId) =>
        pins.some((pin) => String(pin?.id) === String(pinId))
      )
    );
  }, [pins]);

  const selectedPins = pins.filter((pin) =>
    selectedPinIds.some((pinId) => String(pinId) === String(pin?.id))
  );
  const activePins = pins.filter((pin) => pin?.status === "ACTIVE");
  const visibleActivePins = activePins;
  const allVisibleSelected =
    visibleActivePins.length > 0 &&
    visibleActivePins.every((pin) =>
      selectedPinIds.some((pinId) => String(pinId) === String(pin?.id))
    );

  const handleRowClick = (index) => {
    setSelectedRow(selectedRow === index ? null : index);
  };

  const togglePinSelection = (pinId) => {
    setSelectedPinIds((currentSelectedIds) => {
      if (currentSelectedIds.some((id) => String(id) === String(pinId))) {
        return currentSelectedIds.filter((id) => String(id) !== String(pinId));
      }

      return [...currentSelectedIds, pinId];
    });
  };

  const handleSelectAllVisible = () => {
    setSelectedPinIds((currentSelectedIds) => {
      const nextSelectedIds = [...currentSelectedIds];

      visibleActivePins.forEach((pin) => {
        if (!nextSelectedIds.some((id) => String(id) === String(pin?.id))) {
          nextSelectedIds.push(pin.id);
        }
      });

      return nextSelectedIds;
    });
  };

  const handleClearSelection = () => {
    setSelectedPinIds([]);
    setBulkCountInput("");
  };

  const handleSelectByCount = () => {
    if (!selectedAgent) {
      toast.info("Select a fixed agent first", {
        position: "top-left",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
      return;
    }

    const requestedCount = Number.parseInt(bulkCountInput, 10);

    if (Number.isNaN(requestedCount) || requestedCount < 1) {
      toast.error("Enter a valid count greater than 0", {
        position: "top-left",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
      return;
    }

    const orderedPins = [...activePins].sort(sortPinsOldestFirst);
    const limitedPins = orderedPins.slice(0, requestedCount);

    setSelectedPinIds(limitedPins.map((pin) => pin.id));

    if (requestedCount > orderedPins.length) {
      toast.info(
        `Only ${orderedPins.length} active sold pin${
          orderedPins.length === 1 ? "" : "s"
        } available for ${selectedAgent}`,
        {
          position: "top-left",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "dark",
        }
      );
    }
  };

  const openSingleConfirm = (pin) => {
    setConfirmAction({ type: "single", pins: [pin] });
  };

  const openBulkConfirm = () => {
    if (selectedPins.length === 0) {
      toast.info("Select at least one active sold pin", {
        position: "top-left",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
      return;
    }

    setConfirmAction({ type: "bulk", pins: selectedPins });
  };

  const handleConfirmMarkAsUsed = async () => {
    if (!confirmAction?.pins?.length) {
      return;
    }

    const pinsToMark = confirmAction.pins;
    const isBulkAction = confirmAction.type === "bulk";

    if (!isBulkAction) {
      const pin = pinsToMark[0];

      try {
        setMarkingId(pin.id);
        await DistributorApi.markPinAsUsed({
          token,
          pin_id: pin.id,
        });
        logSuccessfulPinUsage(pin);
        toast.success("Pin marked as used successfully", {
          position: "top-left",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "dark",
        });
        setConfirmAction(null);
        fetchPins();
      } catch (error) {
        toast.error(error?.response?.data?.message ?? "Failed to mark pin", {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "dark",
        });
      } finally {
        setMarkingId(null);
      }

      return;
    }

    const failedPins = [];
    let successCount = 0;
    let firstFailureMessage = "";

    try {
      setBulkProcessing(true);
      setBulkProgress({ completed: 0, total: pinsToMark.length });

      for (let index = 0; index < pinsToMark.length; index += 1) {
        const pin = pinsToMark[index];

        try {
          setMarkingId(pin.id);
          await DistributorApi.markPinAsUsed({
            token,
            pin_id: pin.id,
          });
          logSuccessfulPinUsage(pin);
          successCount += 1;
        } catch (error) {
          failedPins.push(pin);

          if (!firstFailureMessage) {
            firstFailureMessage =
              error?.response?.data?.message ?? "Some pins could not be marked";
          }
        } finally {
          setBulkProgress({
            completed: index + 1,
            total: pinsToMark.length,
          });
        }
      }

      if (failedPins.length === 0) {
        toast.success(
          `${successCount} pin${successCount === 1 ? "" : "s"} marked as used`,
          {
            position: "top-left",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "dark",
          }
        );
      } else if (successCount === 0) {
        toast.error(firstFailureMessage, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "dark",
        });
      } else {
        toast.warn(
          `${successCount} pin${successCount === 1 ? "" : "s"} marked as used. ${
            failedPins.length
          } failed and remain selected.`,
          {
            position: "top-left",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "dark",
          }
        );
      }

      setSelectedPinIds(failedPins.map((pin) => pin.id));
      setConfirmAction(null);
      fetchPins();
    } finally {
      setMarkingId(null);
      setBulkProcessing(false);
      setBulkProgress({ completed: 0, total: 0 });
    }
  };

  const selectedAgentCount = selectedAgent ? activePins.length : null;
  const selectionSummary = selectedAgent
    ? `${selectedPins.length} of ${selectedAgentCount ?? 0} pins selected`
    : null;

  return (
    <div>
      <ToastContainer />

      {confirmAction && (
        <div
          className={usedStyles.overlay}
          onClick={() => !bulkProcessing && setConfirmAction(null)}
        >
          <div
            className={usedStyles.confirm_card}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className={usedStyles.confirm_title}>
              {confirmAction.type === "bulk"
                ? "Mark Selected Pins as Used"
                : "Mark Pin as Used"}
            </h2>
            <p className={usedStyles.confirm_desc}>
              {confirmAction.type === "bulk"
                ? `Are you sure you want to mark ${
                    confirmAction.pins.length
                  } pin${
                    confirmAction.pins.length === 1 ? "" : "s"
                  } as used${selectedAgent ? ` for ${selectedAgent}` : ""}?`
                : "Are you sure you want to mark this pin as used?"}
            </p>
            <div className={usedStyles.pin_badge}>
              {confirmAction.type === "bulk"
                ? `${confirmAction.pins.length} selected`
                : confirmAction.pins[0]?.pin}
            </div>
            <div className={usedStyles.confirm_actions}>
              <button
                className={usedStyles.cancel_btn}
                onClick={() => setConfirmAction(null)}
                disabled={markingId !== null || bulkProcessing}
              >
                Cancel
              </button>
              <button
                className={usedStyles.confirm_btn}
                onClick={handleConfirmMarkAsUsed}
                disabled={markingId !== null || bulkProcessing}
              >
                {bulkProcessing || markingId !== null ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={usedStyles.toolbar_row}>
        <select
          className={usedStyles.select_input}
          value={selectedAgent}
          onChange={(event) => setSelectedAgent(event.target.value)}
          disabled={loadingAgents || bulkProcessing}
        >
          <option value="">All active sold pins</option>
          {fixedAgents.map((agent) => (
            <option key={agent.id} value={agent.fullname}>
              {agent.fullname}
            </option>
          ))}
        </select>
      </div>

      {selectedAgent && (
        <div className={usedStyles.bulk_panel}>
          <div className={usedStyles.bulk_header}>
            <p className={usedStyles.selection_summary}>{selectionSummary}</p>
          </div>
          <div className={usedStyles.bulk_actions}>
            <input
              type="number"
              min="1"
              value={bulkCountInput}
              onChange={(event) => setBulkCountInput(event.target.value)}
              placeholder="Select oldest count"
              className={usedStyles.count_input}
              disabled={bulkProcessing}
            />
            <button
              className={`${usedStyles.secondary_btn} ${usedStyles.apply_btn}`}
              onClick={handleSelectByCount}
              disabled={bulkProcessing || activePins.length === 0}
            >
              Apply Count
            </button>
            <button
              className={`${usedStyles.secondary_btn} ${usedStyles.select_btn}`}
              onClick={handleSelectAllVisible}
              disabled={bulkProcessing || visibleActivePins.length === 0}
            >
              {allVisibleSelected ? "All Visible Selected" : "Select Visible"}
            </button>
            <button
              type="button"
              className={usedStyles.clear_icon_btn}
              onClick={handleClearSelection}
              disabled={bulkProcessing || selectedPinIds.length === 0}
              aria-label="Clear selected pins"
              title="Clear selection"
            >
              ×
            </button>
          </div>
          <div className={usedStyles.bulk_actions}>
            <button
              className={`${usedStyles.confirm_btn} ${usedStyles.primary_action_btn}`}
              onClick={openBulkConfirm}
              disabled={bulkProcessing || selectedPins.length === 0}
            >
              Mark Selected as Used
            </button>
          </div>
          {bulkProcessing && (
            <p className={usedStyles.progress_text}>
              Updating pins... {bulkProgress.completed}/{bulkProgress.total}
            </p>
          )}
        </div>
      )}

      <div className={usedStyles.table_wrapper}>
        <table className={usedStyles.table}>
          <thead>
            <tr>
              {selectedAgent && <th>Select</th>}
              <th>S/N</th>
              <th>Pin</th>
              <th>Platform</th>
              <th>Buyer</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loadingPins ? (
              <tr>
                <td colSpan={selectedAgent ? 7 : 6}>
                  <div className={usedStyles.loading_wrapper}>
                    {[...Array(4)].map((_, index) => (
                      <div
                        key={index}
                        className={`${usedStyles.pin_row} ${usedStyles.skeleton}`}
                      />
                    ))}
                  </div>
                </td>
              </tr>
            ) : pins.length === 0 ? (
              <tr>
                <td colSpan={selectedAgent ? 7 : 6}>
                  <div className={usedStyles.empty_state}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#D0D5DD"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M8 12h8M12 8v8"
                        stroke="#D0D5DD"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <p>No active sold pins found</p>
                  </div>
                </td>
              </tr>
            ) : (
              pins.map((item, index) => {
                const isSelected = selectedPinIds.some(
                  (pinId) => String(pinId) === String(item?.id)
                );
                const isExpanded = selectedRow === index;

                return (
                  <React.Fragment key={item.id}>
                    <tr
                      onClick={() => handleRowClick(index)}
                      className={usedStyles.data_row}
                    >
                      {selectedAgent && (
                        <td onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePinSelection(item.id)}
                            disabled={item.status !== "ACTIVE" || bulkProcessing}
                            className={usedStyles.checkbox}
                          />
                        </td>
                      )}
                      <td>{index + 1}</td>
                      <td className={usedStyles.pin_cell}>{item.pin ?? "N/A"}</td>
                      <td>{item.platform ?? "N/A"}</td>
                      <td>{item.agent_name ?? "N/A"}</td>
                      <td>
                        <span
                          className={`${usedStyles.badge} ${
                            item.status === "ACTIVE"
                              ? usedStyles.badge_active
                              : usedStyles.badge_used
                          }`}
                        >
                          {item.status ?? "N/A"}
                        </span>
                      </td>
                      <td onClick={(event) => event.stopPropagation()}>
                        {item.status === "ACTIVE" ? (
                          <button
                            className={usedStyles.mark_btn}
                            onClick={() => openSingleConfirm(item)}
                            disabled={markingId === item.id || bulkProcessing}
                          >
                            {markingId === item.id ? "Processing..." : "Mark as Used"}
                          </button>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={selectedAgent ? 7 : 6}
                          className={usedStyles.details_cell}
                        >
                          <div className={usedStyles.details_grid}>
                            <div>
                              <p className={usedStyles.details_label}>Pin</p>
                              <p className={usedStyles.details_value}>
                                {item.pin ?? "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className={usedStyles.details_label}>Platform</p>
                              <p className={usedStyles.details_value}>
                                {item.platform ?? "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className={usedStyles.details_label}>Buyer</p>
                              <p className={usedStyles.details_value}>
                                {item.agent_name ?? "N/A"}
                              </p>
                            </div>
                            <div>
                              <p className={usedStyles.details_label}>Created</p>
                              <p className={usedStyles.details_value}>
                                {item.created_at ?? "N/A"}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!selectedAgent && (
        <Pagination
          pagination={{ page: currentPage, lastPage: lastPage || 1, total: 0, hasNextPage, hasPreviousPage: hasPrevPage }}
          onPageChange={setCurrentPage}
          isLoading={loadingPins}
          itemLabel="active sold PINs"
        />
      )}
    </div>
  );
}

export default ActiveSoldPins;
